import type { KeyboardEvent } from "react";
import { useCallback } from "react";

import type { IUseGlobalSearchTabKeysProps } from "../types";

const PREVIOUS_KEY = "ArrowLeft";
const NEXT_KEY = "ArrowRight";

// NOTE: NSAI-6853 — ←/→ switch the category tab without leaving the search
// box. They only do so once the caret has nowhere further to go in that
// direction (an empty box, or the caret at either end of the query), so
// moving through the query still works. In a right-to-left box the arrows
// follow the reading direction, as the tab strip does.
export const useGlobalSearchTabKeys = ({
  onSelectAdjacentTab,
}: IUseGlobalSearchTabKeysProps): ((
  event: KeyboardEvent<HTMLDivElement>,
) => void) =>
  useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (event.key !== PREVIOUS_KEY && event.key !== NEXT_KEY) return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }
      const { target } = event;
      if (!(target instanceof HTMLInputElement)) return;
      const { selectionStart, selectionEnd, value } = target;
      if (selectionStart === null || selectionStart !== selectionEnd) return;

      const isRtl = getComputedStyle(target).direction === "rtl";
      const isForward = (event.key === NEXT_KEY) !== isRtl;
      const isAtBoundary = isForward
        ? selectionStart === value.length
        : selectionStart === 0;
      if (!isAtBoundary) return;

      event.preventDefault();
      onSelectAdjacentTab(isForward ? 1 : -1);
    },
    [onSelectAdjacentTab],
  );
